<template>
  <div class="list">
    <div v-if="!embedded" class="breadcrumb">
      <Breadcrumb>
        <Breadcrumb-item>外管工作台</Breadcrumb-item>
        <Breadcrumb-item>主管管理</Breadcrumb-item>
      </Breadcrumb>
    </div>
    <div class="rule-bar">
      <span class="rule-dot"></span>
      提醒规则保存后，工作台、试用期、新外教跟进、合同各页按新规则实时重算。异动的回溯月数、异动线、高危线在本页保存；其余规则仍在「外教异动风险监测」的规则设置里。
    </div>
    <div class="block">
      <div class="block-title">提醒规则</div>
      <Spin v-if="ruleLoading" fix></Spin>
      <Form :label-width="160" :show-message="false" label-position="right" class="rule-form">
        <Form-item v-for="item in fields" :key="item.key" :label="item.label">
          <InputNumber v-model="form[item.key]" :min="item.min" :max="item.max" :step="1"></InputNumber>
          <span class="unit">{{ item.unit }}</span>
          <span class="desc">{{ item.desc }}（{{ item.min }}–{{ item.max }}）</span>
        </Form-item>
        <Form-item label="回访规则上线日">
          <Date-picker type="date" :value="form.reviewStartDate" :clearable="false" transfer
                       style="width: 160px;" @on-change="form.reviewStartDate = $event"></Date-picker>
          <span class="desc">入职日早于该日的老师不进回访清单，避免历史老师全部逾期</span>
        </Form-item>
        <Form-item>
          <Button type="primary" :loading="ruleSaving" @click="saveRules">保存规则</Button>
          <Button style="margin-left: 8px;" @click="loadRules">还原</Button>
        </Form-item>
      </Form>
    </div>
    <div class="block">
      <div class="block-title">异动风险</div>
      <p class="risk-note">其余规则仍在「外教异动风险监测」的规则设置里。</p>
      <Spin v-if="riskLoading" fix></Spin>
      <Form :label-width="160" :show-message="false" label-position="right" class="rule-form">
        <Form-item label="回溯月数">
          <InputNumber v-model="riskForm.lookbackMonths" :min="6" :max="36" :step="1"></InputNumber>
          <span class="unit">个月</span>
          <span class="desc">6–36</span>
        </Form-item>
        <Form-item label="异动线">
          <InputNumber v-model="riskForm.anomalyScore" :min="1" :step="1"></InputNumber>
          <span class="desc">风险分低于该线计入零瑕疵</span>
        </Form-item>
        <Form-item label="高危线">
          <InputNumber v-model="riskForm.high" :min="1" :step="1"></InputNumber>
          <span class="desc">风险分达到该线为高危</span>
        </Form-item>
        <Form-item>
          <Button type="primary" :loading="riskSaving" @click="saveRiskRules">保存异动阈值</Button>
          <Button style="margin-left: 8px;" @click="loadRiskRules">还原</Button>
        </Form-item>
      </Form>
    </div>
    <div class="block">
      <div class="block-title">
        操作日志
        <span class="block-desc">最近 {{ logLimit }} 条</span>
        <Button size="small" icon="refresh" style="margin-left: auto;" @click="loadLogs">刷新</Button>
      </div>
      <Table :columns="logColumns" :data="logList" :loading="logLoading" stripe size="small"
             no-data-text="暂无操作记录"></Table>
    </div>
  </div>
</template>
<script>
import { API_PREFIX, BIZ_TYPE, PROBATION_RESULT, REVIEW_RESULT, FOLLOWUP_RESULT, FOLLOWUP_TYPE, codeText, tchLabel } from './constants'
import { fetchTchScoreMap, applyTchScoresToRows, renderTchNameWithScore } from '@/utils/tchScore'
const FIELDS = [
  { key: 'probationMonths', label: '试用期时长', unit: '个月', min: 1, max: 24, desc: '入职日起试用期长度，到期日按整月对日（月末收敛）' },
  { key: 'probationNearDays', label: '试用期首次提醒', unit: '天', min: 1, max: 90, desc: '到期前多少天进入提醒（须大于二次提醒）' },
  { key: 'probationUrgentDays', label: '试用期二次提醒', unit: '天', min: 1, max: 90, desc: '到期前多少天升级为紧急' },
  { key: 'contractAlertDays', label: '合同提醒提前量', unit: '天', min: 1, max: 365, desc: '合同到期前多少天触发续签提醒' },
  { key: 'reviewDays', label: '新外教回访节点', unit: '天', min: 1, max: 90, desc: '入职后第几天需完成回访' },
  { key: 'reviewAlertDays', label: '回访提前提示', unit: '天', min: 1, max: 90, desc: '节点前多少天开始出现在待回访清单' }
]
const RESULT_DICT = {
  PROBATION: PROBATION_RESULT,
  REVIEW: REVIEW_RESULT,
  CONTRACT: {},
  FOLLOWUP: FOLLOWUP_RESULT
}
function emptyForm () {
  return {
    probationMonths: null,
    probationNearDays: null,
    probationUrgentDays: null,
    contractAlertDays: null,
    reviewDays: null,
    reviewAlertDays: null,
    reviewStartDate: ''
  }
}
function emptyRiskForm () {
  return {
    lookbackMonths: null,
    anomalyScore: null,
    high: null
  }
}
function isInt (v) {
  return typeof v === 'number' && Math.floor(v) === v
}
export default {
  name: 'HrWorkbenchSettings',
  props: {
    embedded: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      fields: FIELDS,
      form: emptyForm(),
      ruleLoading: false,
      ruleSaving: false,
      riskForm: emptyRiskForm(),
      riskLoading: false,
      riskSaving: false,
      logLimit: 50,
      logList: [],
      logLoading: false,
      rankScoreSeq: 0,
      logColumns: []
    }
  },
  created () {
    this.logColumns = [
      { title: '时间', key: 'gmtCreate', width: 160 },
      {
        title: '操作人',
        key: 'creatorName',
        width: 110,
        render: (h, params) => h('span', params.row.creatorName || '—')
      },
      {
        title: '老师',
        key: 'tchName',
        width: 200,
        render: (h, params) => {
          let row = params.row
          return renderTchNameWithScore(h, {
            text: tchLabel(row) + (row.tchId ? '（' + row.tchId + '）' : ''),
            score: row.monthRankScore
          })
        }
      },
      {
        title: '类型',
        key: 'bizType',
        width: 120,
        render: (h, params) => {
          let row = params.row
          let text = BIZ_TYPE[row.bizType] || row.bizType || '—'
          if (row.bizType === 'FOLLOWUP' && row.refType) {
            text += ' · ' + codeText(FOLLOWUP_TYPE, row.refType)
          }
          return h('span', text)
        }
      },
      {
        title: '内容',
        key: 'note',
        width: 280,
        render: (h, params) => {
          let row = params.row
          let parts = []
          let dict = RESULT_DICT[row.bizType] || {}
          if (row.result) {
            parts.push(codeText(dict, row.result))
          }
          if (row.bizType === 'REVIEW' && row.refType) {
            parts.push(row.refType)
          }
          if (row.note) {
            parts.push(row.note)
          }
          return h('span', parts.join(' · ') || '—')
        }
      }
    ]
  },
  mounted () {
    this.loadRules()
    this.loadRiskRules()
    this.loadLogs()
  },
  methods: {
    loadMonthRankScores () {
      const table = this.logList
      if (!table || !table.length) {
        return
      }
      const ids = table.map(function (row) { return row.tchId }).filter(function (id) { return id != null })
      const seq = ++this.rankScoreSeq
      const self = this
      fetchTchScoreMap(this.$http, ids).then(function (map) {
        if (seq !== self.rankScoreSeq) {
          return
        }
        applyTchScoresToRows(self, table, 'tchId', 'monthRankScore', map)
      })
    },

    loadRules () {
      let self = this
      self.ruleLoading = true
      self.$http.post(API_PREFIX + '/rules/get', {}).then(function (res) {
        if (res && res.status === 'ok' && res.data) {
          self.applyRules(res.data)
        }
      }).finally(function () {
        self.ruleLoading = false
      })
    },
    applyRules (data) {
      let form = emptyForm()
      Object.keys(form).forEach(function (k) {
        if (data[k] != null) {
          form[k] = data[k]
        }
      })
      if (form.reviewDays == null) {
        form.reviewDays = 7
      }
      this.form = form
    },
    validate () {
      let f = this.form
      for (let i = 0; i < FIELDS.length; i++) {
        let item = FIELDS[i]
        let v = f[item.key]
        if (!isInt(v) || v < item.min || v > item.max) {
          return item.label + '需为 ' + item.min + '–' + item.max + ' 的整数'
        }
      }
      if (f.probationNearDays <= f.probationUrgentDays) {
        return '试用期首次提醒天数须大于二次提醒天数'
      }
      if (f.reviewAlertDays > f.reviewDays) {
        return '回访提前提示天数须不大于回访节点天数'
      }
      if (!f.reviewStartDate) {
        return '请选择回访规则上线日'
      }
      return ''
    },
    saveRules () {
      let self = this
      let msg = self.validate()
      if (msg) {
        self.$Message.warning(msg)
        return
      }
      self.ruleSaving = true
      self.$http.post(API_PREFIX + '/rules/save', Object.assign({}, self.form)).then(function (res) {
        if (res && res.status === 'ok') {
          self.$Message.success('规则已保存')
          self.$root.$emit('hr-badges-refresh')
          if (res.data) {
            self.applyRules(res.data)
          }
        }
      }).finally(function () {
        self.ruleSaving = false
      })
    },
    loadRiskRules () {
      let self = this
      self.riskLoading = true
      self.$http.post(API_PREFIX + '/risk-rules/get', {}).then(function (res) {
        if (res && res.status === 'ok' && res.data) {
          self.applyRisk(res.data)
        }
      }).finally(function () {
        self.riskLoading = false
      })
    },
    applyRisk (data) {
      let form = emptyRiskForm()
      Object.keys(form).forEach(function (k) {
        if (data[k] != null && data[k] !== '') {
          form[k] = Number(data[k])
        }
      })
      this.riskForm = form
    },
    validateRisk () {
      let f = this.riskForm
      if (!isInt(f.lookbackMonths) || f.lookbackMonths < 6 || f.lookbackMonths > 36) {
        return '回溯月数需为 6–36 的整数'
      }
      if (!isInt(f.anomalyScore) || f.anomalyScore < 1) {
        return '异动线需为正整数'
      }
      if (!isInt(f.high) || f.high < 1) {
        return '高危线需为正整数'
      }
      return ''
    },
    saveRiskRules () {
      let self = this
      let msg = self.validateRisk()
      if (msg) {
        self.$Message.warning(msg)
        return
      }
      self.riskSaving = true
      self.$http.post(API_PREFIX + '/risk-rules/save', {
        lookbackMonths: self.riskForm.lookbackMonths,
        anomalyScore: self.riskForm.anomalyScore,
        high: self.riskForm.high
      }).then(function (res) {
        if (res && res.status === 'ok') {
          self.$Message.success('异动阈值已保存')
          self.$root.$emit('hr-badges-refresh')
          if (res.data) {
            self.applyRisk(res.data)
          }
        }
      }).finally(function () {
        self.riskSaving = false
      })
    },
    loadLogs () {
      let self = this
      self.logLoading = true
      self.$http.post(API_PREFIX + '/log/list', { limit: self.logLimit }).then(function (res) {
        if (res && res.status === 'ok') {
          self.logList = res.data || []
          self.loadMonthRankScores()
        }
      }).finally(function () {
        self.logLoading = false
      })
    }
  }
}
</script>
<style scoped lang="less">
.rule-bar {
  margin: 8px 0 12px;
  padding: 8px 12px;
  background: #f8f8f9;
  border-radius: 4px;
  line-height: 22px;
  font-size: 12px;
  color: #495060;
}
.rule-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 4px;
  border-radius: 50%;
  background: #2d8cf0;
}
.block {
  position: relative;
  margin-bottom: 16px;
  padding: 12px 16px;
  border: 1px solid #e9eaec;
  border-radius: 4px;
}
.block-title {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #495060;
}
.block-desc {
  margin-left: 8px;
  font-size: 12px;
  font-weight: normal;
  color: #80848f;
}
.rule-form {
  max-width: 820px;
}
.unit {
  margin: 0 12px 0 6px;
  color: #495060;
}
.desc {
  margin-left: 8px;
  font-size: 12px;
  color: #80848f;
}
.risk-note {
  margin: 0 0 12px;
  font-size: 12px;
  color: #80848f;
}
</style>



// WEBPACK FOOTER //
// HrWorkbenchSettings.vue?f4179958